import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  QueryList,
  ViewChildren
} from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { Router } from '@angular/router';

import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { FeatherIconsComponent } from '@shared/components/feather-icons/feather-icons.component';
import { UppercaseNoAccentDirective } from '@shared/directives/uppercase-no-accent.directive';

import { MatExpansionModule, MatExpansionPanel } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatAutocompleteModule } from '@angular/material/autocomplete';
import { MatSelectModule } from '@angular/material/select';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatDividerModule } from '@angular/material/divider';
import { MatTooltipModule } from '@angular/material/tooltip';

interface DemoPatientOption {
  id: string;
  fullName: string;
}

interface DemoDiagnosisRow {
  tipo: string;
  nombre: string;
  cronico: boolean;
  codigo: string;
}

interface DemoMedicationRow {
  tipo: string;
  medicamento: string;
  dosis: string;
  frecuencia: string;
  duracion: string;
  via: string;
  motivo: string;
  indicaciones: string;
}

@Component({
  selector: 'app-prescription-edit-demo',
  standalone: true,
  templateUrl: './prescription-edit.component.html',
  styleUrl: './prescription-edit.component.scss',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,

    BreadcrumbComponent,
    FeatherIconsComponent,
    UppercaseNoAccentDirective,

    MatExpansionModule,
    MatFormFieldModule,
    MatInputModule,
    MatAutocompleteModule,
    MatSelectModule,
    MatCheckboxModule,
    MatButtonModule,
    MatIconModule,
    MatDividerModule,
    MatTooltipModule
  ]
})
export class PrescriptionEditComponent implements AfterViewInit {
  @ViewChildren('rxPanel') rxPanels!: QueryList<MatExpansionPanel>;

  breadcrumbs = [
    { label: 'RECETAS', link: '/prescription/prescription-list' },
    { label: 'NUEVA RECETA' }
  ];

  // =========================================
  // MODO DEMO
  // =========================================
  isEditMode = false;

  // =========================================
  // BANDERAS DEMO DEL TUTORIAL
  // =========================================
  showPatientAutocomplete = false;
  patientSelected = false;

  panelPacienteOpen = true;
  panelConsultaOpen = false;
  panelDiagnosticoOpen = false;
  panelMedicamentosOpen = false;
  panelAlertasOpen = false;

  showDiagnosisAutocomplete = false;
  showAllergyAutocomplete = false;
  showMedicationAutocomplete = false;

  showDiagnosisTable = false;
  showMedicationTable = false;

  showPosologyModal = false;

  embarazo = true;
  lactancia = false;
  showWeeksInput = true;

  // =========================================
  // DEMO DATA
  // =========================================
  patientSearch = 'MARIA';
  selectedPatientName = 'MARIANA PACIEWNTE TEST';

  patientOptions: DemoPatientOption[] = [
    {
      id: '1',
      fullName: 'MARIANA PACIEWNTE TEST'
    }
  ];

  consultationData = {
    genero: 'FEMENINO',
    edad: '30 AÑOS 6 MESES',
    peso: '60',
    altura: '165',
    creatinina: '120',
    semanas: '12'
  };

  diagnosisSearch = '';
  allergySearch = '';

  diagnosisRows: DemoDiagnosisRow[] = [
    {
      tipo: 'PATOLOGIA',
      nombre: 'NEUMONIA DEBIDA A VARICELA',
      cronico: true,
      codigo: 'B01.2'
    }
  ];

  medicationSearch = 'PARACETA';

  medicationAutocompleteOptions: string[] = [
    'IBUPROFENO 200 MG/10 ML + PARACETAMOL 250 MG/10 ML SUSPENSIÓN ORAL (ACCIOGEN 2 G/2,5 G/100 ML SUSP. ORAL)',
    'IBUPROFENO 200 MG + PARACETAMOL 500 MG COMPRIMIDO (ACCIOGEN 200 MG/500 MG TAB.)',
    'PARACETAMOL 500 MG COMPRIMIDO (ACETAFEN 500 MG TAB.)',
    'PARACETAMOL 750 MG COMPRIMIDO (ACETAFEN 750 MG TAB.)',
    'PARACETAMOL 500 MG COMPRIMIDO (ACETIF 500 MG TAB.)'
  ];

  medicationRows: DemoMedicationRow[] = [
    {
      tipo: 'PRODUCTO',
      medicamento: 'PARACETAMOL 500 MG COMPRIMIDO (ACETAFEN 500 MG TAB.)',
      dosis: '1',
      frecuencia: 'CADA 8 HORA(S)',
      duracion: '7 DIA(S)',
      via: 'ORAL',
      motivo: 'FIEBRE',
      indicaciones: 'TOMAR CON LECHE'
    }
  ];

  // =========================================
  // POSOLOGÍA DEMO
  // =========================================
  posologia = {
    medicamento: 'PARACETAMOL 500 MG COMPRIMIDO (ACETAFEN 500 MG TAB.)',
    cantidad: '1',
    unidad: 'COMPRIMIDO',
    cada: '8',
    frecuencia: 'HORA(S)',
    duracion: '7',
    periodo: 'DIA(S)',
    via: 'ORAL',
    motivo: 'FIEBRE',
    indicaciones: 'TOMAR CON LECHE'
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private router: Router
  ) {
    this.isEditMode = this.router.url.includes('/prescription/tutorial-edit');

    if (this.isEditMode) {
      this.breadcrumbs = [
        { label: 'RECETAS', link: '/prescription/prescription-list' },
        { label: 'EDITAR RECETA' }
      ];
    }
  }

  ngAfterViewInit(): void {
    if (this.isEditMode) {
      this.showEditFlow();
    } else {
      this.showPatientFlow();
    }

    this.rxPanels.changes.subscribe(() => {
      this.forceOpenVisiblePanels();
    });
  }

  private forceOpenVisiblePanels(): void {
    this.cdr.detectChanges();

    setTimeout(() => {
      this.rxPanels?.forEach((panel) => panel.open());
      this.cdr.detectChanges();
    }, 0);

    setTimeout(() => {
      this.rxPanels?.forEach((panel) => panel.open());
      this.cdr.detectChanges();
    }, 150);
  }

  private openPanelsAfterPatientSelection(): void {
    this.panelPacienteOpen = true;
    this.panelConsultaOpen = true;
    this.panelDiagnosticoOpen = true;
    this.panelMedicamentosOpen = true;
    this.panelAlertasOpen = true;

    this.showDiagnosisTable = true;
    this.showMedicationTable = true;

    this.forceOpenVisiblePanels();
  }

  // =========================================
  // MÉTODOS DEMO
  // =========================================
  openPatientAutocomplete(): void {
    if (this.isEditMode) return;

    this.showPatientAutocomplete = true;
    this.panelPacienteOpen = true;
    this.forceOpenVisiblePanels();
  }

  showPatientFlow(): void {
    this.patientSelected = false;
    this.showPatientAutocomplete = false;

    this.panelPacienteOpen = true;
    this.panelConsultaOpen = false;
    this.panelDiagnosticoOpen = false;
    this.panelMedicamentosOpen = false;
    this.panelAlertasOpen = false;

    this.showDiagnosisTable = false;
    this.showMedicationTable = false;
    this.showPosologyModal = false;

    this.forceOpenVisiblePanels();
  }

  showEditFlow(): void {
    this.patientSelected = true;
    this.showPatientAutocomplete = false;

    this.panelPacienteOpen = true;
    this.panelConsultaOpen = true;
    this.panelDiagnosticoOpen = true;
    this.panelMedicamentosOpen = true;
    this.panelAlertasOpen = true;

    this.showDiagnosisTable = true;
    this.showMedicationTable = true;
    this.showPosologyModal = false;

    this.forceOpenVisiblePanels();
  }

  selectPatient(): void {
    this.patientSelected = true;
    this.showPatientAutocomplete = false;
    this.openPanelsAfterPatientSelection();
  }

  openDiagnosisAutocomplete(): void {
    this.showDiagnosisAutocomplete = true;
    this.panelDiagnosticoOpen = true;
    this.forceOpenVisiblePanels();
  }

  openMedicationAutocomplete(): void {
    this.showMedicationAutocomplete = true;
    this.panelMedicamentosOpen = true;
    this.forceOpenVisiblePanels();
  }

  openPosologyModal(): void {
    this.showPosologyModal = true;
    this.panelMedicamentosOpen = true;
    this.forceOpenVisiblePanels();
  }

  closePosologyModal(): void {
    this.showPosologyModal = false;
  }

  savePosologyDemo(): void {
    this.showPosologyModal = false;
    this.showMedicationTable = true;
    this.panelMedicamentosOpen = true;
    this.forceOpenVisiblePanels();
  }

  deleteMedicationDemo(): void {
    this.showMedicationTable = false;
    this.panelMedicamentosOpen = true;
    this.forceOpenVisiblePanels();
  }

  restoreMedicationDemo(): void {
    this.showMedicationTable = true;
    this.panelMedicamentosOpen = true;
    this.forceOpenVisiblePanels();
  }

  toggleWeeksInput(): void {
    this.showWeeksInput = this.embarazo || this.lactancia;
    this.forceOpenVisiblePanels();
  }

  trackByIndex(index: number): number {
    return index;
  }
}
